import React, {
  forwardRef,
  RefObject,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
} from 'react'
import { Button, Modal } from 'react-bootstrap'
import { VestigingEnum } from 'shared/models/Enum'
import { IAfleverafspraakGroepKoppeling } from 'shared/models/IAfleverafspraakGroepKoppeling'
import { IKlantAfleverafspraak } from 'shared/models/IKlantAfleverafspraak'
import { IKlantGroepModel } from 'shared/models/IKlantGroepModel'
import { IOrderType } from 'shared/models/IOrderType'
import SelectieButton from 'shared/components/SelectieButton'
import PeriodSelect from 'shared/components/PeriodSelect'
import GlnService from 'shared/service/GlnService'
import DateInput from 'shared/components/DateInput'
import {
  convertToDate,
  convertToDateTime,
  selectLongDay,
} from 'shared/service/helpers/DateUtilities'
import {
  IModalReference,
  IModalObjectReference,
} from 'shared/models/References'
import { AflAdrLosseKopersService } from 'services/AflAdrLosseKopersService'
import { IKlant } from 'shared/models/IKlant'

interface IProps {
  customer: IKlant
  service: AflAdrLosseKopersService
  deliveryAgreement: IKlantAfleverafspraak
  onSave?: () => void
  onHide?: () => void
  onLoading?: (isLoading: boolean) => void
  warehouse: VestigingEnum
  possibleOrderTypes: IOrderType[]
}

const getInitialDays = () => {
  return [
    { day: 1, selected: false },
    { day: 2, selected: false },
    { day: 3, selected: false },
    { day: 4, selected: false },
    { day: 5, selected: false },
    { day: 6, selected: false },
    { day: 0, selected: false },
  ]
}

function LinkDeliveryGroup(
  props: IProps,
  ref: RefObject<IModalObjectReference<IAfleverafspraakGroepKoppeling>>
) {
  const [groups, setGroups] = useState<IKlantGroepModel[]>()
  const [orderTypes, setOrderTypes] =
    useState<{ orderType: IOrderType; selected: boolean }[]>()
  const [selectedGroup, setSelectedGroup] = useState<IKlantGroepModel>()
  const [errorMessage, setErrorMessage] = useState<string>()
  const [startDate, setStartDate] = useState<Date>()
  const [endDate, setEndDate] = useState<Date>()
  const [showModal, setShowModal] = useState(false)
  const [internalShowModal, setInternalShowModal] = useState(true)
  const [days, setDays] = useState<{ day: number; selected: boolean }[]>(
    getInitialDays()
  )
  const [existingLink, setExistingLink] =
    useState<IAfleverafspraakGroepKoppeling>()

  const periodRef = useRef<IModalReference>()

  useEffect(() => {
    GlnService.getKlantgroepen(props.warehouse, null).then(result => {
      if (result.success) {
        setGroups(result.payLoad)
        setErrorMessage(null)
      } else {
        setGroups(null)
        setErrorMessage(result.errorMessage)
      }
    })
  }, [props.warehouse])

  useEffect(() => {
    if (!!props.possibleOrderTypes) {
      setOrderTypes(
        props.possibleOrderTypes.map(o => {
          return { orderType: o, selected: false }
        })
      )
    } else {
      setOrderTypes([])
    }
  }, [props.possibleOrderTypes])

  useImperativeHandle(ref, () => ({
    show: (link: IAfleverafspraakGroepKoppeling) => {
      show(link)
    },
    hide: () => {
      hide()
    },
  }))

  const clearState = (link: IAfleverafspraakGroepKoppeling) => {
    setErrorMessage(null)

    if (!!link) {
      setExistingLink(link)
      setSelectedGroup(link.klantGroep)
      setStartDate(convertToDateTime(link.ingangsDatum))
      setEndDate(convertToDateTime(link.eindDatum))

      const dayArray = link.dagen.split(',').map(d => +d)
      days.forEach(d => (d.selected = dayArray.some(a => a === d.day)))
      setDays(days.slice())

      const orderTypeArray = link.orderTypes.split(',').map(o => +o)
      orderTypes.forEach(
        o => (o.selected = orderTypeArray.some(a => a === o.orderType.value))
      )
      setOrderTypes(orderTypes.slice())
    } else {
      setExistingLink(null)
      setSelectedGroup(null)

      const start =
        convertToDate(props.deliveryAgreement.ingangsDatum) > convertToDate()
          ? convertToDate(props.deliveryAgreement.ingangsDatum)
          : new Date()

      setStartDate(start)
      setEndDate(props.deliveryAgreement.eindDatum)
      days.forEach(d => (d.selected = false))
      setDays(days.slice())
      orderTypes.forEach(o => (o.selected = false))
      setOrderTypes(orderTypes.slice())
    }
  }

  const getGroups = () => {
    const selection = [{ code: 0, omschrijving: '' }]
    if (!!groups) {
      ;[].push.apply(
        selection,
        groups.map(g => {
          return {
            code: g.klantGroepCode,
            omschrijving: g.klantGroepOmschrijving,
          }
        })
      )
    }
    return selection
  }

  const setGroup = e => {
    const group = groups.find(g => +g.klantGroepCode === +e.target.value)
    setSelectedGroup(group)
  }

  const onPeriodSelected = (selectedStartDate: Date, selectedEndDate: Date) => {
    setStartDate(selectedStartDate)
    setEndDate(selectedEndDate)
  }

  const hide = () => {
    setShowModal(false)
    if (!!props.onHide) {
      props.onHide()
    }
  }

  const save = () => {
    const link = getNewLink()
    if (!!props.onLoading) {
      props.onLoading(true)
    }

    props.service
      .saveGroupLink(link, props.customer)
      .then(result => {
        if (result.success) {
          setErrorMessage(null)
          if (!!props.onSave) {
            props.onSave()
            hide()
          }
        } else {
          setErrorMessage(result.errorMessage)
        }
      })
      .finally(() => {
        if (!!props.onLoading) {
          props.onLoading(false)
        }
      })
  }

  const show = (link: IAfleverafspraakGroepKoppeling) => {
    if (!showModal) {
      clearState(link)
      setShowModal(true)
    }
  }

  const getNewLink = (): IAfleverafspraakGroepKoppeling => {
    if (!!existingLink) {
      existingLink.ingangsDatum = startDate
      existingLink.eindDatum = endDate

      return existingLink
    } else {
      return {
        afleverafspraakGroepKoppelingId: 0,
        dagen: days
          .filter(d => d.selected)
          .map(d => d.day)
          .join(','),
        ingangsDatum: startDate,
        eindDatum: endDate,
        klantAfleverAfspraakId: props.deliveryAgreement.klantAfleverafspraakId,
        klantGroepId: selectedGroup.klantGroepId,
        orderTypes: orderTypes
          .filter(o => o.selected)
          .map(o => o.orderType.value)
          .join(','),
        klantGroep: selectedGroup,
        klantAfleverafspraak: props.deliveryAgreement,
      }
    }
  }

  const canChangeStartDate = (): boolean => {
    return (
      !existingLink ||
      convertToDate(existingLink.ingangsDatum) > convertToDate()
    )
  }

  const renderOrdertypes = () => {
    if (!orderTypes) {
      return <div></div>
    }

    return <div className='row'>{orderTypes.map(o => renderOrderType(o))}</div>
  }

  const renderDays = () => {
    if (!days) {
      return <div></div>
    }

    return (
      <div className='row'>
        {renderDay(days[0])}
        {renderDay(days[3])}
        {renderDay(days[5])}
        {renderDay(days[1])}
        {renderDay(days[4])}
        {renderDay(days[6])}
        {renderDay(days[2])}
      </div>
    )
  }

  const renderDay = (d: { day: number; selected: boolean }) => {
    return (
      <div className='col-4' key={`val_d_${d.day}`}>
        <input
          id={`box_${d.day}`}
          key={`box_${d.day}`}
          type='checkbox'
          value={d.day}
          checked={d.selected}
          onChange={() => selectDayOption(d)}
          disabled={!!existingLink}
        />
        <label key={`lab_${d.day}`} className='ml-1' htmlFor={`box_${d.day}`}>
          {selectLongDay(d.day)}
        </label>
      </div>
    )
  }

  const renderOrderType = (o: { orderType: IOrderType; selected: boolean }) => {
    return (
      <div className='col-12' key={`val_${o.orderType.value}`}>
        <input
          id={`box_${o.orderType.value}`}
          key={`box_${o.orderType.value}`}
          type='checkbox'
          value={o.orderType.value}
          checked={o.selected}
          onChange={() => selectOrderType(o)}
          disabled={!!existingLink}
        />
        <label
          key={`lab_${o.orderType.value}`}
          className='ml-1'
          htmlFor={`box_${o.orderType.value}`}
        >
          {o.orderType.name} ({o.orderType.value})
        </label>
      </div>
    )
  }

  const selectOrderType = (o: { orderType: IOrderType; selected: boolean }) => {
    o.selected = !o.selected
    const temp = orderTypes.slice()
    setOrderTypes(temp)
  }

  const selectDayOption = (d: { day: number; selected: boolean }) => {
    d.selected = !d.selected
    const temp = days.slice()
    setDays(temp)
  }

  const hideDateModel = () => {
    setInternalShowModal(true)
  }

  const showDateModel = () => {
    setInternalShowModal(false)
  }

  const noValidLink = (): boolean => {
    return !selectedGroup
  }

  return (
    <React.Fragment>
      {showModal && (
        <Modal
          show={internalShowModal}
          onHide={hide}
          animation={false}
          size='lg'
        >
          <Modal.Header closeButton>
            <Modal.Title>Koppelen aan Afleverlocatie-groep</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {errorMessage != null && (
              <div className='alert alert-danger'>{errorMessage}</div>
            )}
            <div className='row border mb-2 p-2 mx-1'>
              <div className='col-2'>
                <h5>Afleverafspraak</h5>
              </div>
              <div className='col-3'>
                <div className='text-right font-weight-bold'>Referentie</div>
              </div>
              <div className='col-7'>
                {props.deliveryAgreement.afleverafspraakReferentie}
              </div>
              <div className='col-5'>
                <div className='text-right font-weight-bold'>Naam</div>
              </div>
              <div className='col-7'>{props.deliveryAgreement.naam}</div>
              <div className='col-5'>
                <div className='text-right font-weight-bold'>Klant</div>
              </div>
              <div className='col-7'>{props.customer.klantNaam}</div>
            </div>
            <div className='row border border-bottom-0 mx-1 pt-2 px-2'>
              <div className='col-2'>
                <h5>Afleverlocatie-groep</h5>
              </div>
              <div className='col-3'>
                <div className='text-right font-weight-bold'>Naam</div>
              </div>
              <div className='col-7'>
                <SelectieButton
                  labelClassName={'w-100'}
                  className={'no-max w-100'}
                  readonly={!!existingLink}
                  type={'klantgroep'}
                  options={getGroups()}
                  selected={!!selectedGroup ? selectedGroup.klantGroepCode : 0}
                  onChange={e => setGroup(e)}
                />
              </div>
            </div>
            {!!selectedGroup && (
              <div className='row border border-top-0 mx-1 mb-2 pb-2 px-2'>
                <div className='col-5'>
                  <div className='text-right font-weight-bold'>Vestiging</div>
                </div>
                <div className='col-7'>
                  {VestigingEnum[selectedGroup.vestigingCode.valueOf()]}
                </div>
                <div className='col-5'>
                  <div className='text-right font-weight-bold'>Code</div>
                </div>
                <div className='col-7'>{selectedGroup.klantGroepCode}</div>
                <div className='col-5'>
                  <div className='text-right font-weight-bold'>LocatieGLN</div>
                </div>
              </div>
            )}
            {!selectedGroup && (
              <div className='row border border-top-0 mb-2 mx-1 pb-2 px-2'></div>
            )}
            <div className='row border mb-2 p-2 mx-1'>
              <div className='col-2'>
                <h5>Periode</h5>
              </div>
              <div className='col-3'>
                <div className='text-right font-weight-bold'>Ingangsdatum</div>
              </div>
              <div className='col-7 mb-1'>
                <DateInput
                  date={startDate}
                  onDateChanged={setStartDate}
                  disabled={!canChangeStartDate()}
                />
              </div>
              <div className='col-5'>
                <div className='text-right font-weight-bold'>Einddatum</div>
              </div>
              <div className='col'>
                <DateInput date={endDate} onDateChanged={setEndDate} />
              </div>
              <div className='col-auto'>
                <Button
                  variant='primary'
                  onClick={() => periodRef.current.show()}
                >
                  Kies periode
                </Button>
              </div>
            </div>
            <div className='row border mb-2 p-2 mx-1'>
              <div className='col-5'>
                <h5>OrderTypes</h5>
              </div>
              <div className='col-7'>{renderOrdertypes()}</div>
            </div>
            <div className='row border mb-2 p-2 mx-1'>
              <div className='col-5'>
                <h5>Dagen</h5>
              </div>
              <div className='col-7'>{renderDays()}</div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='primary' onClick={save} disabled={noValidLink()}>
              Opslaan
            </Button>
            <Button variant='secondary' onClick={hide}>
              Sluiten
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <PeriodSelect
        ref={periodRef}
        initialStartDate={startDate}
        initialEndDate={endDate}
        maxEndDate={props.deliveryAgreement?.eindDatum ? new Date(props.deliveryAgreement.eindDatum) : undefined}
        disabledStartDate={!canChangeStartDate()}
        onPeriodSelected={onPeriodSelected}
        onHide={hideDateModel}
        onShow={showDateModel}
        canClose={true}
      />
    </React.Fragment>
  )
}
export default forwardRef(LinkDeliveryGroup)
